import * as React from 'react';
import clsx from 'clsx';
import { styled, useTheme, useThemeProps } from '@mui/material/styles';
import composeClasses from '@mui/utils/composeClasses';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import type { CSSTransitionProps } from 'react-transition-group/CSSTransition';
import type { TransitionGroupProps } from 'react-transition-group/TransitionGroup';
import type { PickersSlideTransitionClasses } from './pickersSlideTransitionClasses';
import {
  getPickersSlideTransitionUtilityClass,
  pickersSlideTransitionClasses,
} from './pickersSlideTransitionClasses';
import type { PickerOwnerState } from '../models/pickers';
import { usePickerPrivateContext } from '../internals/hooks/usePickerPrivateContext';

export type SlideDirection = 'right' | 'left';

export interface PickerSlideTransitionOwnerState extends PickerOwnerState {
  slideDirection: SlideDirection;
}

export interface ExportedSlideTransitionProps {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<PickersSlideTransitionClasses>;
}
export interface SlideTransitionProps
  extends Omit<CSSTransitionProps, 'timeout'>, ExportedSlideTransitionProps {
  children: React.ReactElement<any>;
  className?: string;
  reduceAnimations: boolean;
  slideDirection: SlideDirection;
  transKey: React.Key;
}

const useUtilityClasses = (
  classes: Partial<PickersSlideTransitionClasses> | undefined,
  ownerState: PickerSlideTransitionOwnerState,
) => {
  const { slideDirection } = ownerState;

  const slots = {
    root: ['root'],
    exit: ['slideExit'],
    enterActive: ['slideEnterActive'],
    enter: [`slideEnter-${slideDirection}`],
    exitActive: [`slideExitActiveLeft-${slideDirection}`],
  };

  return composeClasses(slots, getPickersSlideTransitionUtilityClass, classes);
};

const elementOverrides = [
  'slideEnter-left',
  'slideEnter-right',
  'slideEnterActive',
  'slideExit',
  'slideExitActiveLeft-left',
  'slideExitActiveLeft-right',
] as const;

const PickersSlideTransitionRoot = styled(TransitionGroup, {
  name: 'MuiPickersSlideTransition',
  slot: 'Root',
  overridesResolver: (_, styles) =>
    elementOverrides.reduce(
      (acc, key) => {
        acc.push({ [`.${pickersSlideTransitionClasses[key]}`]: styles[key] });
        return acc;
      },
      [styles.root],
    ),
})<TransitionGroupProps & { ownerState?: PickerSlideTransitionOwnerState }>(({ theme }) => {
  const slideTransition = theme.transitions.create('transform', {
    duration: theme.transitions.duration.complex,
    easing: 'cubic-bezier(0.35, 0.8, 0.4, 1)',
  });
  return {
    display: 'block',
    position: 'relative',
    overflowX: 'hidden',
    '& > *': {
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
    },
    [`& .${pickersSlideTransitionClasses['slideEnter-left']}`]: {
      willChange: 'transform',
      transform: 'translate(100%)',
      zIndex: 1,
    },
    [`& .${pickersSlideTransitionClasses['slideEnter-right']}`]: {
      willChange: 'transform',
      transform: 'translate(-100%)',
      zIndex: 1,
    },
    [`& .${pickersSlideTransitionClasses.slideEnterActive}`]: {
      transform: 'translate(0%)',
      transition: slideTransition,
    },
    [`& .${pickersSlideTransitionClasses.slideExit}`]: {
      transform: 'translate(0%)',
    },
    [`& .${pickersSlideTransitionClasses['slideExitActiveLeft-left']}`]: {
      willChange: 'transform',
      transform: 'translate(-100%)',
      transition: slideTransition,
      zIndex: 0,
    },
    [`& .${pickersSlideTransitionClasses['slideExitActiveLeft-right']}`]: {
      willChange: 'transform',
      transform: 'translate(100%)',
      transition: slideTransition,
      zIndex: 0,
    },
  };
});

/**
 * @ignore - do not document.
 */
export function PickersSlideTransition(inProps: SlideTransitionProps) {
  const props = useThemeProps({ props: inProps, name: 'MuiPickersSlideTransition' });
  const {
    children,
    className,
    reduceAnimations,
    slideDirection,
    transKey,
    classes: classesProp,
    ...other
  } = props;

  const { ownerState: pickerOwnerState } = usePickerPrivateContext();
  const ownerState = { ...pickerOwnerState, slideDirection };
  const classes = useUtilityClasses(classesProp, ownerState);
  const theme = useTheme();
  if (reduceAnimations) {
    // `role="none"` keeps the day rows owned by the calendar grid.
    return (
      <div role="none" className={clsx(classes.root, className)}>
        {children}
      </div>
    );
  }

  const transitionClasses = {
    exit: classes.exit,
    enterActive: classes.enterActive,
    enter: classes.enter,
    exitActive: classes.exitActive,
  };

  return (
    <PickersSlideTransitionRoot
      className={clsx(classes.root, className)}
      childFactory={(element: React.ReactElement<any>) =>
        React.cloneElement(element, {
          classNames: transitionClasses,
        })
      }
      role="none"
      ownerState={ownerState}
    >
      <CSSTransition
        mountOnEnter
        unmountOnExit
        key={transKey}
        timeout={theme.transitions.duration.complex}
        classNames={transitionClasses}
        {...other}
      >
        {children}
      </CSSTransition>
    </PickersSlideTransitionRoot>
  );
}
